import { Injectable, inject } from "@angular/core";
import { collection, Firestore, getDocs, where, query, doc, getDoc } from "@angular/fire/firestore";
import { UserRole } from "../models/permissions.model";
import { Auth } from "@angular/fire/auth";

export interface PermissionCheck {
	hasAccess: boolean;
	roles: UserRole[];
	data?: any;
}

@Injectable({
	providedIn: "root",
})
export class PermissionsService {
	private firestore = inject(Firestore);
	private auth = inject(Auth);
	permissions = UserRole;

	constructor() {}

	// Global admin status is read from users/{uid}.roles.global_admin (single source of truth).
	// An inactive account (active === false) has NO global access even with the role set.
	async getGlobalPermissions(userId: string): Promise<PermissionCheck> {
		const userDoc = await getDoc(doc(this.firestore, "users", userId));
		const data = userDoc.exists() ? userDoc.data() : undefined;
		const isAdmin =
			!!data && data["roles"]?.["global_admin"] === true && data["active"] !== false;
		return {
			hasAccess: isAdmin,
			roles: isAdmin ? [UserRole.ADMIN] : [],
			data,
		};
	}

	// Get organization permissions with role check
	async getOrganizationPermissions(
		organizationId: string,
		userId: string,
	): Promise<PermissionCheck> {
		const organizationMembersCollection = collection(this.firestore, "organizationUsers");
		const q = query(
			organizationMembersCollection,
			where("organization_id", "==", organizationId),
			where("user_id", "==", userId),
			where("active", "==", true),
		);
		const snapshot = await getDocs(q);
		if (snapshot.empty) {
			return { hasAccess: false, roles: [] };
		}
		const data = snapshot.docs[0].data();
		const roles = this.rolesFromMap(data["roles"] || {});
		return { hasAccess: true, roles, data };
	}

	// Check if user has access to any organization
	async hasSomeOrganizationAccess(userId: string): Promise<PermissionCheck> {
		const organizationMembersCollection = collection(this.firestore, "organizationUsers");
		const q = query(
			organizationMembersCollection,
			where("user_id", "==", userId),
			where("active", "==", true),
		);
		const snapshot = await getDocs(q);
		if (snapshot.empty) {
			return { hasAccess: false, roles: [] };
		}
		const data = snapshot.docs[0].data();
		const roles = this.rolesFromMap(data["roles"] || {});
		return { hasAccess: true, roles, data };
	}

	// Check if user has any type of access (global or organization)
	async hasAnyAccess(userId: string): Promise<PermissionCheck> {
		const [globalResult, orgResult] = await Promise.all([
			this.getGlobalPermissions(userId),
			this.hasSomeOrganizationAccess(userId),
		]);

		if (globalResult.hasAccess) {
			return globalResult;
		}

		if (orgResult.hasAccess) {
			return orgResult;
		}

		return { hasAccess: false, roles: [] };
	}

	// Get all organizations user has access to
	async getAllOrganizationAccess(userId: string): Promise<PermissionCheck[]> {
		const organizationMembersCollection = collection(this.firestore, "organizationUsers");
		const q = query(
			organizationMembersCollection,
			where("user_id", "==", userId),
			where("active", "==", true),
		);
		const snapshot = await getDocs(q);
		return snapshot.docs.map((d) => {
			const data = d.data();
			const roles = this.rolesFromMap(data["roles"] || {});
			return { hasAccess: true, roles, data: { ...data, id: d.id } };
		});
	}

	// Check if user has specific role in organization
	async hasOrganizationRole(
		organizationId: string,
		userId: string,
		role: UserRole,
	): Promise<boolean> {
		const result = await this.getOrganizationPermissions(organizationId, userId);
		return result.hasAccess && result.roles.includes(role);
	}

	// Check if user is global admin. An inactive account (active === false) is NOT a
	// global admin even with the role set.
	async isGlobalAdmin(userId: string): Promise<boolean> {
		const userDoc = await getDoc(doc(this.firestore, "users", userId));
		if (!userDoc.exists()) {
			return false;
		}

		const userData = userDoc.data();
		const roles = userData["roles"] || {};
		return roles["global_admin"] === true && userData["active"] !== false;
	}

	// Derive the list of granted roles from an organizationUsers roles map,
	// keeping only keys that are valid UserRole values (ignores unknown keys).
	private rolesFromMap(perms: Record<string, unknown>): UserRole[] {
		const valid = Object.values(UserRole) as string[];
		return Object.keys(perms)
			.filter((key) => perms[key] && valid.includes(key))
			.map((key) => key as UserRole);
	}

	// Get user-friendly message for permission errors
	getPermissionErrorMessage(context: "global" | "organization" | "organizations"): string {
		switch (context) {
			case "global":
				return "You don't have the correct permissions to access this page.";
			case "organization":
				return "You don't have permission to edit this organization.";
			case "organizations":
				return "You don't have permission to edit any organizations.";
			default:
				return "You don't have sufficient permissions to access this page.";
		}
	}
}
